














































































































































import { AxiosError } from 'axios';
import moment from 'moment-timezone';
import { mapActions, mapGetters } from 'vuex';

import {
  EmployeeFormSubmissionStateEnum,
  FormSubmissionChecklistStateEnum,
  IEmployeeFormSubmission,
  IEmployeeFormSubmissionChecklist,
  IEmployeeFormSubmissionData,
  IFormTemplate,
  IFormTemplateData
} from '@/models';
import { ErrorManager } from '@/models/error';

import { createPdf } from '@/utils/helpers';
import { classToHex, colors } from '@/utils/colorUtils';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import ALPage from '@/components/core/Page/ALPage.vue';
import DetailOptionsButton from '@/components/common/DetailOptionsButton.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import EmployeeFormSubmissionEmployeeCard from '@/components/employee-form/EmployeeFormSubmissionEmployeeCard.vue';
import EmployeeFormSubmissionCommentCard from '@/components/employee-form/EmployeeFormSubmissionCommentCard.vue';
import EmployeeFormSubmissionDetail from '@/components/employee-form/EmployeeFormSubmissionDetail.vue';
import EmployeeFormSubmissionDepartmentCard from '@/components/employee-form/EmployeeFormSubmissionDepartmentCard.vue';

export default mixins(auth, snackbar).extend({
  name: 'EmployeeFormSubmissionDetailPage',

  components: {
    ALPage,
    DetailOptionsButton,
    EmployeeFormSubmissionEmployeeCard,
    EmployeeFormSubmissionCommentCard,
    ErrorAlert,
    EmployeeFormSubmissionDepartmentCard,
    EmployeeFormSubmissionDetail
  },
  data() {
    return {
      loading: false,
      commentLoading: false,
      isExportLoading: false,

      errorMessage: '',
      errorMessageDetail: '',

      submission: {} as IEmployeeFormSubmissionData,
      template: null as IFormTemplate | null
    };
  },

  computed: {
    ...mapGetters({
      getFormTemplateById: 'formTemplate/getFormTemplateById',
      getEmployeeFormSubmissionById:
        'employeeForm/getEmployeeFormSubmissionById',
      getEmployeeFormSubmissionChecklistsByDepartment:
        'employeeForm/getEmployeeFormSubmissionChecklistsByDepartment',
      checklists: 'employeeForm/getEmployeeFormSubmissionChecklists',
      departments: 'employeeForm/getEmployeeFormSubmissionDepartments'
    }),

    employeeFormSubmissionId(): string {
      return this.$route.params.id;
    },

    employeeFormSubmission(): IEmployeeFormSubmission {
      return this.getEmployeeFormSubmissionById(
        Number(this.employeeFormSubmissionId)
      );
    },

    formTemplate(): IFormTemplate | undefined {
      return (
        this.submission &&
        this.getFormTemplateById((this.submission.form as IFormTemplateData).id)
      );
    },

    getStatusHexColor(): string {
      return classToHex(
        this.employeeFormSubmission!.getStateInfo().color,
        colors,
        this.$vuetify.theme.currentTheme
      );
    },

    isFormCompleted(): boolean {
      let isComplete = false;

      if (this.employeeFormSubmission && this.checklists.length > 0) {
        isComplete = (this
          .checklists as IEmployeeFormSubmissionChecklist[]).every(
          checklist =>
            checklist.state != FormSubmissionChecklistStateEnum.InProgress
        );
      }
      return isComplete;
    },

    isOwner(): boolean {
      return this.getCurrentUserFullName == this.employeeFormSubmission!.owner;
    },

    canEdit(): boolean {
      let edit = false;

      if (this.submission.state == EmployeeFormSubmissionStateEnum.Draft) {
        edit = this.isOwner;
      }

      if (this.submission.state == EmployeeFormSubmissionStateEnum.InReview) {
        edit = this.isOwner || this.userHasRoles('IT Admin');
      }

      return edit;
    },

    canCancel(): boolean {
      let cancellable = false;

      if (this.submission.state == EmployeeFormSubmissionStateEnum.Draft) {
        cancellable = this.isOwner;
      }

      if (
        this.submission.state == EmployeeFormSubmissionStateEnum.InReview ||
        this.submission.state == EmployeeFormSubmissionStateEnum.InProgress
      ) {
        cancellable = this.userHasRoles('IT Admin');
      }

      if (this.authProvider.isUserAdmin) {
        cancellable = true;
      }

      if (this.submission.state == EmployeeFormSubmissionStateEnum.Cancelled) {
        cancellable = false;
      }

      return cancellable;
    },

    showCommentSection(): boolean {
      return (
        this.submission.state != EmployeeFormSubmissionStateEnum.Draft &&
        this.submission.state != EmployeeFormSubmissionStateEnum.InReview
      );
    }
  },

  watch: {
    isFormCompleted: {
      async handler(value) {
        if (
          this.employeeFormSubmission!.state !==
          EmployeeFormSubmissionStateEnum.Cancelled
        ) {
          if (value) {
            this.employeeFormSubmission!.state =
              EmployeeFormSubmissionStateEnum.Completed;
          } else {
            const originalState = await (
              await this.tryGetOrFetchEmployeeFormSubmission()
            ).state;

            this.submission.state =
              originalState == EmployeeFormSubmissionStateEnum.Completed
                ? EmployeeFormSubmissionStateEnum.InProgress
                : originalState;
          }
        }
      }
    },

    //to maintain reactivity with the changing of state and formsubmission updates
    employeeFormSubmission: {
      handler(value) {
        this.submission = value;
      },
      immediate: true
    }
  },

  async mounted() {
    this.loading = true;
    try {
      await this.tryGetOrFetchEmployeeFormSubmission();

      await this.tryGetOrFetchFormTemplate();

      await this.refreshChecklist();
    } catch (err) {
      const error = err as AxiosError;
      if (error.response) {
        if (error.response.data.detail) {
          this.errorMessageDetail = error.response.data.detail;
        }
      }

      this.errorMessage = ErrorManager.extractApiError(error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions({
      fetchEmployeeFormSubmission: 'employeeForm/fetchEmployeeFormSubmission',
      fetchEmployeeFormSubmissionComments:
        'employeeForm/fetchEmployeeFormSubmissionComments',
      fetchEmployeeFormSubmissionDepartments:
        'employeeForm/fetchEmployeeFormSubmissionDepartments',
      fetchEmployeeFormSubmissionChecklists:
        'employeeForm/fetchEmployeeFormSubmissionChecklists',
      fetchFormTemplate: 'formTemplate/fetchFormTemplate',
      exportEmployeeFormSubmissionPDF:
        'employeeForm/exportEmployeeFormSubmissionPDF'
    }),

    async tryGetOrFetchEmployeeFormSubmission() {
      if (!this.employeeFormSubmission) {
        await this.fetchEmployeeFormSubmission(
          Number(this.employeeFormSubmissionId)
        );
      }
      return this.employeeFormSubmission;
    },

    async tryGetOrFetchFormTemplate(): Promise<IFormTemplate> {
      if (!this.formTemplate) {
        await this.fetchFormTemplate(
          (this.submission.form as IFormTemplateData).id
        );
      }
      return this.formTemplate!;
    },

    async refreshComments() {
      this.commentLoading = true;
      try {
        await this.fetchEmployeeFormSubmissionComments(this.submission.id);
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
        }

        this.errorMessage = ErrorManager.extractApiError(error);
      } finally {
        this.commentLoading = false;
      }
    },

    async exportSubmissionPDF() {
      this.clearError();
      this.isExportLoading = true;
      try {
        const pdfBlobData = await this.exportEmployeeFormSubmissionPDF(
          this.submission.id
        );
        const fileName = `form-submission-#${this.submission.id}-${moment(
          new Date()
        ).format('YYYY-MM-DD-HH-mm-ss')}`;
        createPdf(pdfBlobData, fileName);

        this.createSnack({
          color: 'success darken-1',
          type: 'success',
          right: true,
          useBtn: false,
          msg: `PDF attachment downloaded successfully`,
          btnText: 'Ok',
          timeout: 6000
        });
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
        }

        this.errorMessage = ErrorManager.extractApiError(error);
      } finally {
        this.isExportLoading = false;
      }
    },

    async refreshChecklist() {
      await this.fetchEmployeeFormSubmissionDepartments(
        this.employeeFormSubmission
      );

      await this.fetchEmployeeFormSubmissionChecklists(
        this.employeeFormSubmission
      );
    }
  }
});
